/**
 * Prioris — Spec Compliance card inside the MA submittal drawer.
 *
 * When a submittal's material_family matches a table in the project's
 * spec-catalog selections, this card renders the SAME structure as the
 * published spec (property rows × selected-rubrica columns) but with
 * editable inputs per property. Each cell shows V/NV live against the
 * rubrica's requirement.
 *
 * Inputs are persisted to submittal.custom_fields via the existing MA
 * PATCH endpoint — no new data model required.
 */
import { useEffect, useMemo, useState } from 'react';
import { fetchSln } from '../nexus/nexusHelpers';
import { Loader2, Check, XCircle, CircleSlash, HelpCircle, PenSquare, ShieldCheck, Info } from 'lucide-react';

const VERDICT_BASE = {
  V:       { bg: 'rgba(34,197,94,0.15)',  color: '#22c55e', label: 'V',          icon: Check },
  NV:      { bg: 'rgba(239,68,68,0.15)',  color: '#f87171', label: 'NV',         icon: XCircle },
  NA:      { bg: 'rgba(148,163,184,0.1)', color: '#94a3b8', label: 'NA',         icon: CircleSlash },
  declare: { bg: 'rgba(234,179,8,0.15)',  color: '#eab308', label: 'To be declared', icon: PenSquare },
  missing: { bg: 'rgba(234,179,8,0.2)',   color: '#f59e0b', label: '?',          icon: HelpCircle },
  partial: { bg: 'rgba(234,179,8,0.2)',   color: '#f59e0b', label: 'Partial',    icon: HelpCircle },
};
// Helper that returns the verdict map localised for this table's jurisdiction.
// PT-EP keeps the Portuguese labels ("A declarar", "Parcial"); all others use English.
function verdictMap(table) {
  if (table?.jurisdiction_key === 'PT-EP') {
    return {
      ...VERDICT_BASE,
      declare: { ...VERDICT_BASE.declare, label: 'A declarar' },
      partial: { ...VERDICT_BASE.partial, label: 'Parcial' },
    };
  }
  return VERDICT_BASE;
}

export default function SpecCatalogComplianceCard({ t, row, onReload }) {
  const [matchingSelections, setMatchingSelections] = useState(null); // array or null

  // Load ALL matching project selections for this submittal's family
  useEffect(() => {
    if (!row?.project_id) return;
    fetchSln(`/api/prioris/spec-catalog/project/${row.project_id}/selections`)
      .then(r => {
        const all = r.selections || [];
        const matching = all.filter(s => s.family === row.material_family && (s.selected_rubricas || []).length > 0);
        setMatchingSelections(matching);
      })
      .catch(() => setMatchingSelections([]));
  }, [row?.project_id, row?.material_family]);

  if (!row?.project_id) return null;
  if (matchingSelections === null) {
    return (
      <div className="rounded p-3 text-[11px]" style={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.textDim }}>
        <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading project specs…
      </div>
    );
  }
  if (matchingSelections.length === 0) {
    return (
      <div className="rounded p-2" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }} data-testid="spec-compliance-card-empty">
        <div className="flex items-start gap-2 text-[11px]">
          <ShieldCheck className="w-3 h-3 mt-0.5" style={{ color: t.textDim }} />
          <div>
            <div className="font-semibold" style={{ color: t.text }}>Spec Compliance</div>
            <div style={{ color: t.textDim }}>
              No project specs selected for family <code>{row.material_family}</code>. Go to <strong>Spec Library</strong> to select the applicable clauses first.
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="spec-compliance-card-multi">
      {matchingSelections.length > 1 && (
        <div className="rounded p-2 flex items-start gap-2 text-[11px]"
          style={{ background: 'rgba(34,197,94,0.08)', border: `1px solid rgba(34,197,94,0.25)`, color: t.textMuted }}
          data-testid="spec-compliance-multi-hint">
          <Info className="w-3 h-3 mt-0.5" style={{ color: '#22c55e' }} />
          <span>
            This submittal is evaluated against <strong>{matchingSelections.length} tables</strong> ({matchingSelections.map(s => s.table_key).join(' · ')}) — all tables must pass for a conforming RAM.
          </span>
        </div>
      )}
      {matchingSelections.map(sel => (
        <SpecComplianceTableSection key={sel.table_key} t={t} row={row} selection={sel} onReload={onReload} />
      ))}
    </div>
  );
}

function SpecComplianceTableSection({ t, row, selection, onReload }) {
  const [table, setTable] = useState(null);
  const [result, setResult] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!selection?.table_key) return;
    fetchSln(`/api/prioris/spec-catalog/tables/${selection.table_key}`).then(setTable).catch(() => setTable(null));
  }, [selection?.table_key]);

  const cf = row?.custom_fields || {};
  const actualPayload = useMemo(() => {
    const out = {};
    for (const k of Object.keys(cf)) {
      const v = cf[k];
      if (v === '' || v === null || v === undefined) continue;
      if (typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v)) out[k] = Number(v);
      else out[k] = v;
    }
    return out;
  }, [cf]);

  // Narrow to the submittal's own clause when it has one assigned — this
  // prevents the compliance matrix from spraying every grade/column across
  // the screen when the submittal is for a specific clause (e.g. 16-G35).
  // Falls back to all project-selected clauses when no spec_rubrica_code is set.
  const projectCodes = selection.selected_rubricas || [];
  const rowRubricaCode = row?.spec_rubrica_code;
  const activeCodes = useMemo(
    () => (rowRubricaCode && projectCodes.includes(rowRubricaCode))
      ? [rowRubricaCode]
      : projectCodes,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rowRubricaCode, projectCodes.join('|')]
  );

  useEffect(() => {
    if (!table || !selection) { setResult(null); return; }
    fetchSln('/api/prioris/spec-catalog/evaluate', {
      method: 'POST',
      body: JSON.stringify({
        table_key: table.key,
        rubrica_codes: activeCodes,
        actual: actualPayload,
      }),
    }).then(setResult).catch(() => setResult(null));
  }, [table, selection, actualPayload, activeCodes]);

  if (!table) {
    return (
      <div className="rounded p-3 text-[11px]" style={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.textDim }}>
        <Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading {selection.table_key}…
      </div>
    );
  }

  const visibleRubs = table.rubricas.filter(r => activeCodes.includes(r.code));

  const startEdit = (fieldKey) => {
    setEditing(fieldKey);
    const v = cf[fieldKey];
    setDraft(v === undefined || v === null ? '' : String(v));
  };

  const commit = async (fieldKey, nextValue) => {
    setSaving(true);
    try {
      const newCf = { ...(row.custom_fields || {}) };
      if (nextValue === '' || nextValue === null || nextValue === undefined) {
        delete newCf[fieldKey];
      } else {
        newCf[fieldKey] = nextValue;
      }
      await fetchSln(`/api/prioris/ma/submittals/${row.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ custom_fields: newCf }),
      });
      setEditing(null);
      if (onReload) await onReload();
    } catch { /* ignore */ }
    setSaving(false);
  };

  const overall = result?.overall;
  const verdicts = verdictMap(table);
  const ov = overall ? verdicts[overall] : null;

  return (
    <div className="rounded p-2" style={{ background: t.card, border: `1px solid ${t.cardBorder}` }} data-testid={`spec-compliance-card-${table.key}`}>
      {/* Header */}
      <div className="flex items-start gap-2 mb-2">
        <ShieldCheck className="w-3.5 h-3.5 mt-0.5" style={{ color: t.accent }} />
        <div className="flex-1 min-w-0">
          <div className="text-[12px] font-bold" style={{ color: t.text }}>Spec Compliance · {table.key}</div>
          <div className="text-[10px]" style={{ color: t.textDim }}>
            {table.jurisdiction_label} · {table.document_label} · {table.standard}
          </div>
        </div>
        {ov && (
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded" style={{ background: ov.bg, color: ov.color }} data-testid={`spec-compliance-overall-${table.key}`}>
            <ov.icon className="w-3 h-3" />
            <span className="text-[11px] font-bold">{ov.label}</span>
          </div>
        )}
      </div>

      {result && (
        <div className="mb-2 text-[10px] flex gap-3" style={{ color: t.textDim }}>
          <span><span style={{ color: '#22c55e' }}>V:</span> {result.tally.V}</span>
          <span><span style={{ color: '#f87171' }}>NV:</span> {result.tally.NV}</span>
          <span><span style={{ color: '#94a3b8' }}>NA:</span> {result.tally.NA}</span>
          <span><span style={{ color: '#eab308' }}>{verdicts.declare.label}:</span> {result.tally.declare}</span>
          <span><span style={{ color: '#f59e0b' }}>?:</span> {result.tally.missing}</span>
        </div>
      )}

      {/* Matrix */}
      <div className="overflow-x-auto rounded" style={{ border: `1px solid ${t.cardBorder}` }}>
        <table className="w-full text-[11px]" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
          <thead>
            <tr style={{ background: t.inputBg }}>
              <th className="text-left px-2 py-1.5 sticky left-0 z-10" style={{ background: t.inputBg, borderBottom: `1px solid ${t.cardBorder}`, minWidth: 180 }}>
                <div className="font-bold" style={{ color: t.text }}>{table.jurisdiction_key === 'PT-EP' ? 'Propriedade' : 'Property'}</div>
              </th>
              <th className="px-2 py-1.5 text-center" style={{ borderBottom: `1px solid ${t.cardBorder}`, minWidth: 100, color: t.textMuted }}>{table.jurisdiction_key === 'PT-EP' ? 'Medido' : 'Measured'}</th>
              {visibleRubs.map(r => (
                <th key={r.code} className="px-2 py-1.5 text-center" style={{ borderBottom: `1px solid ${t.cardBorder}`, minWidth: 90 }}>
                  <div className="font-mono font-bold" style={{ color: t.accent }}>{r.code}</div>
                  <div className="text-[9px] leading-tight" style={{ color: t.textDim }}>{r.label}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.properties.map((p, i) => (
              <PropertyRow key={p.key} t={t}
                verdicts={verdicts}
                prop={p}
                rowEven={i % 2 === 0}
                visibleRubs={visibleRubs}
                cf={cf}
                result={result}
                editing={editing}
                draft={draft}
                setDraft={setDraft}
                startEdit={startEdit}
                commit={commit}
                saving={saving}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* Sonnenbrand toggle — only relevant for PT-EP aggregate rules */}
      {table.jurisdiction_key === 'PT-EP' && <SonnenbrandToggle t={t} row={row} onReload={onReload} />}

      {/* Footnotes */}
      {table.footnotes && Object.keys(table.footnotes).length > 0 && (
        <div className="mt-2 text-[10px]" style={{ color: t.textDim }}>
          {Object.entries(table.footnotes).map(([k, v]) => (
            <div key={k}><strong style={{ color: t.accent }}>({k})</strong> {v}</div>
          ))}
        </div>
      )}

      <div className="mt-2 text-[10px] flex items-start gap-1" style={{ color: t.textDim }}>
        <Info className="w-2.5 h-2.5 mt-0.5" />
        <span>Click any "{table.jurisdiction_key === 'PT-EP' ? 'Medido' : 'Measured'}" cell to enter a value. Enter = save · Esc = cancel · Tab = next.</span>
      </div>
    </div>
  );
}

function PropertyRow({ t, verdicts, prop, rowEven, visibleRubs, cf, result, editing, draft, setDraft, startEdit, commit, saving }) {
  const rowResult = result?.rows?.find(r => r.key === prop.key);
  const measuredKey = prop.eval_field;
  const measuredValue = measuredKey ? cf[measuredKey] : undefined;
  const isEditing = editing === measuredKey;

  // Additional helper inputs for conditional rules
  const helperFields = (() => {
    if (prop.key === 'fines_quality_se_mb') return [
      { key: 'passante_0063', label: 'Passante 0,063 mm (%)' },
      { key: 'se',             label: 'SE' },
      { key: 'mb',             label: 'MB (g/kg)' },
    ];
    if (prop.key === 'sonnenbrand') return [
      { key: 'sonnenbrand_mass_loss', label: 'Perda de massa (%)' },
      { key: 'sbla',                   label: 'SBLA (%)' },
    ];
    if (prop.key === 'freeze_thaw') return [
      { key: 'water_absorption', label: 'WA₂₄ (%)' },
      { key: 'ms_value',         label: 'MS (%)' },
    ];
    return null;
  })();

  // Detect computed-sum rows (cells tagged conditional=sum_of on any visible
  // rubrica) so the Measured column shows a read-only "auto = X" instead of
  // an editable input. The actual sum is already returned by the backend
  // evaluator in rowResult.per_rubrica[...].actual as "auto: N unit".
  const isComputedSum = visibleRubs.some(r => prop.values?.[r.code]?.conditional === 'sum_of');

  const computedActual = (() => {
    if (!isComputedSum || !rowResult) return null;
    for (const r of visibleRubs) {
      const cell = rowResult.per_rubrica?.[r.code];
      if (cell?.actual && typeof cell.actual === 'string' && cell.actual.startsWith('auto')) return cell.actual;
    }
    return null;
  })();

  const bg = rowEven ? 'transparent' : 'rgba(255,255,255,0.02)';

  return (
    <tr>
      <td className="px-2 py-1.5 sticky left-0" style={{ background: rowEven ? 'var(--ma-card)' : bg, borderBottom: `1px solid ${t.cardBorder}`, verticalAlign: 'top' }}>
        <div className="font-semibold leading-tight" style={{ color: t.text }}>{prop.label}</div>
        <div className="text-[9px] mt-0.5" style={{ color: t.textDim }}>
          {prop.ref_normativa} · {prop.unit}
        </div>
      </td>

      {/* Measured input cell */}
      <td className="px-1 py-1.5 text-center align-top" style={{ borderBottom: `1px solid ${t.cardBorder}`, verticalAlign: 'top' }}>
        {isComputedSum ? (
          <div
            className="w-full text-center py-0.5 rounded"
            style={{
              background: 'rgba(34,197,94,0.08)',
              border: `1px dashed rgba(34,197,94,0.35)`,
              color: t.text, fontSize: 11, fontStyle: 'italic',
            }}
            title="Auto-computed from fly ash + silica fume + slag + HRP"
            data-testid={`spec-computed-${measuredKey}`}>
            {computedActual || 'auto —'}
          </div>
        ) : helperFields ? (
          <div className="space-y-0.5">
            {helperFields.map(hf => (
              <InlineInput key={hf.key} t={t}
                field={hf.key} label={hf.label}
                value={cf[hf.key] ?? ''}
                onCommit={(v) => commit(hf.key, v)}
                saving={saving}
              />
            ))}
          </div>
        ) : (
          isEditing ? (
            <input autoFocus type="text" value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter')  commit(measuredKey, draft);
                if (e.key === 'Escape') { setDraft(''); commit(measuredKey, cf[measuredKey] ?? ''); }
              }}
              onBlur={() => commit(measuredKey, draft)}
              className="w-full text-center rounded px-1 py-0.5 text-[12px]"
              style={{ background: t.inputBg, border: `1px solid ${t.accent}`, color: t.text }}
              disabled={saving}
              data-testid={`spec-input-${measuredKey}`}
            />
          ) : (
            <button onClick={() => startEdit(measuredKey)}
              className="w-full text-center py-0.5 rounded transition-colors"
              style={{ background: t.inputBg, border: `1px dashed ${t.inputBorder}`, color: measuredValue === undefined || measuredValue === '' ? t.textDim : t.text }}
              data-testid={`spec-edit-${measuredKey}`}>
              {measuredValue === undefined || measuredValue === '' ? '—' : String(measuredValue)}
            </button>
          )
        )}
      </td>

      {/* One V/NV cell per rubrica */}
      {visibleRubs.map(r => {
        const c = rowResult?.per_rubrica?.[r.code];
        if (!c) {
          return <td key={r.code} className="px-2 py-1.5 text-center" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textDim }}>…</td>;
        }
        const v = verdicts[c.status] || verdicts.missing;
        return (
          <td key={r.code} className="px-2 py-1.5 text-center" style={{ borderBottom: `1px solid ${t.cardBorder}`, verticalAlign: 'top' }}>
            <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
              style={{ background: v.bg, color: v.color }}
              title={`${c.expected}\nActual: ${c.actual}${c.message ? '\n' + c.message : ''}`}
              data-testid={`spec-verdict-${prop.key}-${r.code}`}>
              <v.icon className="w-2.5 h-2.5" />
              <span className="font-bold text-[11px]">{v.label}</span>
            </div>
            <div className="text-[9px] mt-0.5" style={{ color: t.textDim }}>{c.expected}</div>
          </td>
        );
      })}
    </tr>
  );
}

function InlineInput({ t, field, label, value, onCommit, saving }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  useEffect(() => { setDraft(value ?? ''); }, [value]);
  if (!editing) {
    return (
      <button onClick={() => setEditing(true)}
        className="w-full text-[10px] py-0.5 rounded flex items-center justify-between gap-1 px-1"
        style={{ background: t.inputBg, border: `1px dashed ${t.inputBorder}` }}
        data-testid={`spec-inline-${field}`}>
        <span style={{ color: t.textDim }}>{label}:</span>
        <span style={{ color: value === '' || value === undefined ? t.textDim : t.text }}>{value === '' || value === undefined ? '—' : value}</span>
      </button>
    );
  }
  return (
    <div className="flex items-center gap-1 text-[10px]">
      <span style={{ color: t.textDim, minWidth: 'fit-content' }}>{label}:</span>
      <input autoFocus type="text" value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { setEditing(false); onCommit(draft); }
          if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
        }}
        onBlur={() => { setEditing(false); onCommit(draft); }}
        className="flex-1 w-0 rounded px-1 text-center text-[11px]"
        style={{ background: t.inputBg, border: `1px solid ${t.accent}`, color: t.text }}
        disabled={saving}
      />
    </div>
  );
}

function SonnenbrandToggle({ t, row, onReload }) {
  const cf = row.custom_fields || {};
  const v = !!cf.sonnenbrand_suspected;
  const toggle = async () => {
    const newCf = { ...cf, sonnenbrand_suspected: !v };
    await fetchSln(`/api/prioris/ma/submittals/${row.id}`, {
      method: 'PATCH',
      body: JSON.stringify({ custom_fields: newCf }),
    });
    if (onReload) await onReload();
  };
  return (
    <label className="mt-2 inline-flex items-center gap-1.5 text-[11px] cursor-pointer" style={{ color: t.textMuted }} data-testid="spec-sonnenbrand-toggle">
      <input type="checkbox" checked={v} onChange={toggle} />
      Indícios de Sonnenbrand (desbloqueia os ensaios específicos)
    </label>
  );
}
